import { useEffect, useState } from 'react';
import type {
  SatelliteIndex,
  SatelliteObservation,
  UserRole,
} from '@greeniq/shared';
import {
  getObservations,
  refreshZone,
  type ObservationsResponse,
} from '../api/satellite.js';
import { ApiError } from '../api/client.js';

const REFRESH_ROLES: UserRole[] = ['ageye_admin', 'superintendent', 'assistant_super'];
const DISPLAY_INDICES: readonly SatelliteIndex[] = [
  'ndvi',
  'ndre',
  'gndvi',
  'savi',
  'msavi',
  'ndwi',
] as const;
const INDEX_LABEL: Record<SatelliteIndex, string> = {
  ndvi: 'NDVI',
  ndre: 'NDRE',
  gndvi: 'GNDVI',
  savi: 'SAVI',
  msavi: 'MSAVI',
  ndwi: 'NDWI',
};
const INDEX_TAGLINE: Record<SatelliteIndex, string> = {
  ndvi: 'Vigor',
  ndre: 'Chlorophyll / N',
  gndvi: 'High-biomass vigor',
  savi: 'Soil-adjusted (L=0.5)',
  msavi: 'Soil-adjusted (auto)',
  ndwi: 'Moisture',
};

interface Props {
  courseSlug: string;
  zoneId: number;
  role: UserRole | undefined;
}

export function ZoneSatelliteCard({ courseSlug, zoneId, role }: Props) {
  const canRefresh = role ? REFRESH_ROLES.includes(role) : false;
  const [latest, setLatest] = useState<SatelliteObservation | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);

  async function load() {
    try {
      const resp: ObservationsResponse = await getObservations(courseSlug, zoneId);
      const obs = resp.observations;
      setLatest(obs.length > 0 ? obs[obs.length - 1]! : null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, [courseSlug, zoneId]);

  async function onRefresh() {
    setRefreshing(true);
    setBanner(null);
    try {
      const result = await refreshZone(courseSlug, zoneId);
      if (result.processingMethod === 'node_sh_stats') {
        setBanner('Geoprocessing service unavailable — used Sentinel Hub fallback (6× quota).');
      } else if (result.degraded) {
        setBanner(`${result.skippedDates.length} windows skipped (clouds or gaps).`);
      }
      if (result.observations.length > 0) {
        setLatest(result.observations[result.observations.length - 1]!);
      } else {
        await load();
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setBanner('Too soon — wait 30 seconds before refreshing again.');
      } else {
        setBanner(err instanceof ApiError ? err.message : String(err));
      }
    } finally {
      setRefreshing(false);
    }
  }

  return (
    <section className="mt-8 p-5 bg-bg-card rounded-lg border border-border">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-sm uppercase tracking-wider text-fg-muted font-semibold">
            Satellite indices
          </h2>
          {latest && (
            <p className="mt-1 text-xs text-fg-muted">
              Acquired {latest.observationDate} · {latest.sampleCount} pixels ·{' '}
              {Number(latest.cloudCoverPct).toFixed(1)}% cloud
            </p>
          )}
        </div>
        {canRefresh && (
          <button
            onClick={onRefresh}
            disabled={refreshing}
            className="min-h-[44px] px-4 py-2 text-sm rounded-md bg-greeniq-500 text-[#0b1014] font-semibold hover:bg-greeniq-400 disabled:opacity-50"
          >
            {refreshing ? 'Fetching…' : 'Refresh'}
          </button>
        )}
      </div>

      {banner && (
        <div className="mt-3 text-xs px-3 py-2 rounded-md bg-greeniq-500/10 text-greeniq-400">
          {banner}
        </div>
      )}

      {loading ? (
        <p className="mt-4 text-sm text-fg-muted">Loading…</p>
      ) : !latest ? (
        <div className="mt-4">
          <p className="text-sm text-fg-muted">
            No satellite data yet for this zone.
            {canRefresh
              ? ' Click Refresh to fetch the last 30 days.'
              : ' A superintendent can trigger the first refresh.'}
          </p>
        </div>
      ) : (
        <>
          {latest.isLowConfidence && (
            <div className="mt-3 text-xs px-3 py-2 rounded-md bg-greeniq-500/10 text-greeniq-400">
              Low confidence — edge noise, cloud coverage, or few pixels. Showing median values,
              which are robust to outliers.
            </div>
          )}
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
            {DISPLAY_INDICES.map((idx) => (
              <IndexChip key={idx} index={idx} obs={latest} />
            ))}
          </div>
        </>
      )}
    </section>
  );
}

function IndexChip({
  index,
  obs,
}: {
  index: SatelliteIndex;
  obs: SatelliteObservation;
}) {
  const medianKey = `${index}Median` as const;
  const value = (obs as unknown as Record<string, number | null>)[medianKey];
  return (
    <div className="p-3 rounded-md bg-bg-elevated min-h-[44px]">
      <div className="flex items-baseline justify-between">
        <span className="text-xs uppercase tracking-wider text-fg-muted font-semibold">
          {INDEX_LABEL[index]}
        </span>
        <span className="text-lg font-semibold text-greeniq-300">
          {value === null || value === undefined ? '—' : Number(value).toFixed(3)}
        </span>
      </div>
      <div className="mt-0.5 text-[10px] text-fg-muted">{INDEX_TAGLINE[index]}</div>
    </div>
  );
}
